import type { UUID } from '@protector/utils/types';
import type { StaticPointsDateRange } from 'entities/static-points/static-points.models';
import axios from 'axios-observable';
import { CHUNK_ANALYTICS_RESULTS, PROTECTOR_API_URL } from 'config/constants';
import { getObservables } from 'core/rxjs-utils';
import type { GetStaticPointResponse } from '@protector/entities/static-point/models';
import moment from 'moment';
import type { Observable } from 'rxjs';
import { EMPTY, of, throwError } from 'rxjs';
import { catchError, concatMap, expand, map, toArray } from 'rxjs/operators';
import type { AnalyticResultsHistogramResponse, Station, StationActionType, StationResponse } from './stations.models';

interface ChunkOutcome {
  failed: boolean;
  severities: AnalyticResultsHistogramResponse[];
}

export interface PaginatedAnalyticResultsHistogram {
  severities: AnalyticResultsHistogramResponse[][];
  hasFailedChunks: boolean;
}

const CHUNK_PAGES_ANALYTICS_RESULTS_HISTOGRAM = 1;
const MAX_CHUNK_PAGES_ANALYTICS_RESULTS_HISTOGRAM = 8;
const CHUNK_ANALYTICS_RESULTS_HISTOGRAM_IDS = 200;
const STATIONS_PAGE_SIZE = 3000;
const protectorApiUrl = PROTECTOR_API_URL;
const nanoBaseUrl = `${protectorApiUrl}/api/v1`;
const archeBaseUrl = `${protectorApiUrl}/v1`;
const staticPointUrl = `${nanoBaseUrl}/properties`;
const trapUrl = `${archeBaseUrl}/timeline/trap`;
const trapsUrl = `${archeBaseUrl}/timeline/traps`;
const severityUrl = `${trapsUrl}/monitoring`;
const staticPointsUrl = `${archeBaseUrl}/timeline/static-points`;

export const getStaticPointsByPage = (
  property_idParam: string,
  is_last = true,
  cursor = '',
  dateRange?: StaticPointsDateRange
): Observable<StationResponse> => {
  const cursorParam = !is_last && cursor ? `&cursor=${cursor}` : '';
  const lastEventToParam = dateRange?.endDate ? `&last_event_to=${dateRange.endDate}` : '';
  const dateRangeParams = dateRange ? `&last_event_from=${dateRange.startDate}${lastEventToParam}` : '';
  const params = `size=${STATIONS_PAGE_SIZE}${cursorParam}${dateRangeParams}`;
  return axios
    .get<StationResponse>(`${staticPointUrl}/${property_idParam}/stations?${params}`, {
      headers: { 'Content-Type': 'application/json' }
    })
    .pipe(map(response => response.data));
};

export const getAllStations = (property_idParam: string, dateRange?: StaticPointsDateRange): Observable<Station[]> => {
  return getStaticPointsByPage(property_idParam, true, '', dateRange).pipe(
    expand(({ is_last, cursor }) => (!is_last ? getStaticPointsByPage(property_idParam, is_last, cursor, dateRange) : EMPTY)),
    concatMap(({ content }) => content),
    toArray()
  );
};

export const getAnalyticResultsHistogram = (
  property_idParam: string,
  start_dateParam: Date,
  end_DateParam: Date,
  template_idParam: string,
  ids: string[],
  all_actions = false,
  include_points_without_monitoring = false
): Observable<AnalyticResultsHistogramResponse[]> => {
  const start = moment(start_dateParam).format('YYYY-MM-DD');
  const end = moment(end_DateParam).format('YYYY-MM-DD');
  const params = `?property_id=${property_idParam}&start_date=${start}&end_date=${end}&template_id=${template_idParam}&all_actions=${all_actions}&include_points_without_monitoring=${include_points_without_monitoring}`;
  return axios.post<AnalyticResultsHistogramResponse[]>(`${severityUrl}${params}`, ids).pipe(
    map(r => r.data),
    catchError(r => throwError(r))
  );
};

const collectChunkOutcomes = (outcomeGroups: ChunkOutcome[][]): PaginatedAnalyticResultsHistogram => {
  const outcomes = outcomeGroups.flat();
  return {
    severities: outcomes.flatMap(outcome => (outcome.failed ? [] : [outcome.severities])),
    hasFailedChunks: outcomes.some(outcome => outcome.failed)
  };
};

export const getAnalyticResultsHistogramIdsPaginated = (
  property_idParam: string,
  start_dateParam: Date,
  end_DateParam: Date,
  template_idParam: string,
  ids: string[],
  all_actions = false,
  {
    includePointsWithoutMonitoring = false,
    chunkPages = CHUNK_PAGES_ANALYTICS_RESULTS_HISTOGRAM
  }: { includePointsWithoutMonitoring?: boolean; chunkPages?: number } = {}
): Observable<PaginatedAnalyticResultsHistogram> => {
  const CHUNK = all_actions ? CHUNK_ANALYTICS_RESULTS : CHUNK_ANALYTICS_RESULTS_HISTOGRAM_IDS;
  const CHUNK_PAGES = Number.isFinite(chunkPages)
    ? Math.min(MAX_CHUNK_PAGES_ANALYTICS_RESULTS_HISTOGRAM, Math.max(1, Math.floor(chunkPages)))
    : CHUNK_PAGES_ANALYTICS_RESULTS_HISTOGRAM;
  const totalChunkedPages = Math.floor(ids.length / CHUNK + 1);
  const pages = [...new Array(totalChunkedPages).keys()];

  const observables = pages.map(page => {
    const idsChunked = ids.slice(page * CHUNK, (page + 1) * CHUNK);
    return getAnalyticResultsHistogram(
      property_idParam,
      start_dateParam,
      end_DateParam,
      template_idParam,
      idsChunked,
      all_actions,
      includePointsWithoutMonitoring
    ).pipe(
      map((severities): ChunkOutcome => ({ failed: false, severities })),
      catchError(() => of<ChunkOutcome>({ failed: true, severities: [] }))
    );
  });

  const totalChunckObservable = Math.floor(totalChunkedPages / CHUNK_PAGES + 1);
  const observablesPages = [...new Array(totalChunckObservable).keys()];
  const observableMap = observablesPages.map(oPage => {
    return getObservables(observables.slice(oPage * CHUNK_PAGES, (oPage + 1) * CHUNK_PAGES), true);
  });

  return getObservables(observableMap, false).pipe(map(collectChunkOutcomes));
};

export const updateStation = (
  actionType: StationActionType,
  account: { id: UUID; name: string },
  stationId: UUID,
  companyId: UUID,
  propertyId: UUID,
  autoCreateTask = false
) => {
  return axios.put(staticPointsUrl, {
    account,
    payload: {
      action_type: actionType,
      id: stationId,
      company_id: companyId,
      property_id: propertyId,
      auto_create_task: autoCreateTask
    }
  });
};

export const editStation = (account: { id: UUID; name: string }, station: GetStaticPointResponse) => {
  return axios.put(trapUrl, {
    account,
    payload: station
  });
};
